import React, { Children } from 'react';
import { Avatar, Box, Card, CardContent, Button, Stack, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';

type InfoCardRootProps = {
    children: React.ReactNode;
    columns?: number;
    maxAutoColumns?: number;
};

type InfoCardBackButtonProps = React.ComponentProps<typeof Button> & {
    icon?: React.ReactNode;
};

type InfoCardAvatarProps = {
    children?: React.ReactNode;
    sx?: React.ComponentProps<typeof Avatar>['sx'];
};

type InfoCardTitleProps = {
    children: React.ReactNode;
    subtitle?: React.ReactNode;
};

type InfoCardItemProps = {
    label?: React.ReactNode;
    children: React.ReactNode;
};

// Sub-component containers
const HeaderBox: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <Box
        sx={{
            px: 2.5,
            py: 1.5,
            bgcolor: 'grey.50',
            borderBottom: 1,
            borderColor: 'grey.100'
        }}
    >
        {children}
    </Box>
);

type ItemsGridProps = {
    columns: number;
    children: React.ReactNode;
};

const ItemsGrid: React.FC<ItemsGridProps> = ({ columns, children }) => (
    <Box
        sx={{
            display: 'grid',
            gap: 2,
            gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, minmax(0, 1fr))',
                md: columns
                    ? `repeat(${columns}, minmax(0, 1fr))`
                    : 'repeat(auto-fit, minmax(180px, 1fr))'
            }
        }}
    >
        {children}
    </Box>
);

type ItemContainerProps = {
    label?: React.ReactNode;
    children: React.ReactNode;
};

const ItemContainer: React.FC<ItemContainerProps> = ({ label, children }) => (
    <Box>
        {label && (
            <Typography
                variant="overline"
                color="text.secondary"
                sx={{
                    display: 'block',
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: 1
                }}
            >
                {label}
            </Typography>
        )}

        <Box sx={{ pt: label ? 0.5 : 0 }}>{children}</Box>
    </Box>
);

// Sub-components
const InfoCardBackButton = (_props: InfoCardBackButtonProps) => null;
InfoCardBackButton.displayName = 'InfoCard.BackButton';

const InfoCardAvatar = (_props: InfoCardAvatarProps) => null;
InfoCardAvatar.displayName = 'InfoCard.Avatar';

const InfoCardTitle = (_props: InfoCardTitleProps) => null;
InfoCardTitle.displayName = 'InfoCard.Title';

const InfoCardItem = (_props: InfoCardItemProps) => null;
InfoCardItem.displayName = 'InfoCard.Item';

const isType = <P,>(
    node: React.ReactNode,
    component: React.ComponentType<P>
): node is React.ReactElement<P> => {
    return React.isValidElement(node) && node.type === component;
};

type InfoCardCompoundComponent = React.FC<InfoCardRootProps> & {
    BackButton: React.FC<InfoCardBackButtonProps>;
    Avatar: React.FC<InfoCardAvatarProps>;
    Title: React.FC<InfoCardTitleProps>;
    Item: React.FC<InfoCardItemProps>;
};

const InfoCardRoot: React.FC<InfoCardRootProps> = ({ children, columns, maxAutoColumns = 5 }) => {
    const nodes = Children.toArray(children);

    const backButton = nodes.find((n) => isType(n, InfoCardBackButton)) as
        | React.ReactElement<InfoCardBackButtonProps>
        | undefined;

    const avatar = nodes.find((n) => isType(n, InfoCardAvatar)) as
        | React.ReactElement<InfoCardAvatarProps>
        | undefined;

    const title = nodes.find((n) => isType(n, InfoCardTitle)) as
        | React.ReactElement<InfoCardTitleProps>
        | undefined;

    const items = nodes.filter((n) =>
        isType(n, InfoCardItem)
    ) as React.ReactElement<InfoCardItemProps>[];

    const resolvedColumns = columns ?? Math.min(Math.max(items.length, 1), maxAutoColumns);
    const navigate = useNavigate();
    return (
        <Card variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
            <HeaderBox>
                <Stack direction="row" spacing={2} alignItems="center">
                    {backButton && (
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={() => navigate(-1)}
                            sx={{
                                minWidth: 0,
                                p: 1,
                                mr: 2,
                                ...(backButton.props.sx ?? {})
                            }}
                            {...backButton.props}
                        >
                            {backButton.props.icon ?? <span aria-hidden>←</span>}
                        </Button>
                    )}

                    {avatar && (
                        <Avatar sx={{ width: 40, height: 40, ...avatar.props.sx }}>
                            {avatar.props.children}
                        </Avatar>
                    )}

                    {title && (
                        <Box>
                            <Typography variant="subtitle1" fontWeight={700} lineHeight={1.2}>
                                {title.props.children}
                            </Typography>

                            {title.props.subtitle && (
                                <Typography variant="caption" color="text.secondary">
                                    {title.props.subtitle}
                                </Typography>
                            )}
                        </Box>
                    )}
                </Stack>
            </HeaderBox>

            <CardContent sx={{ p: 2.5 }}>
                <ItemsGrid columns={resolvedColumns}>
                    {items.map((item, index) => (
                        <ItemContainer
                            key={item.props.label?.toString() ?? index}
                            label={item.props.label}
                        >
                            {item.props.children}
                        </ItemContainer>
                    ))}
                </ItemsGrid>
            </CardContent>
        </Card>
    );
};

export const InfoCardCompound = Object.assign(InfoCardRoot, {
    BackButton: InfoCardBackButton,
    Avatar: InfoCardAvatar,
    Title: InfoCardTitle,
    Item: InfoCardItem
}) as InfoCardCompoundComponent;

export default InfoCardCompound;
